import { useWizardContext } from '@service-victoria/component-library/components/Wizard/context';
import { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';

import { app as Context } from 'app/context';
import * as Steps from 'app/transactions/provider/steps';
import { userManager } from 'app/services';

const stepsWhichNeedIdToken = [
  Steps.ProviderDetails.Step.id,
  Steps.Review.Step.id,
  Steps.Done.Step.id
];

export default function useStepValidation() {
  const {
    currentState: { idToken }
  } = Context.useAppContext();
  const { formValues, currentStepId, dispatch } = useWizardContext();
  const { state } = useLocation();

  const navigateToStep = useCallback(
    (stepId: string) => {
      if (stepId !== currentStepId)
        dispatch({
          type: 'GOTO_STEP',
          stepId: stepId,
          values: formValues
        });
    },
    [currentStepId, dispatch, formValues]
  );

  const validateStep = useCallback(
    async (schema: Yup.ObjectSchema<any>, stepIfFailed: string) => {
      try {
        await schema.validate(formValues);
      } catch {
        navigateToStep(stepIfFailed);
      }
    },
    [formValues, navigateToStep]
  );

  const goToSignIn = useCallback(async () => {
    await userManager.startUserSignin();
  }, []);

  return useEffect(() => {
    /*
     * Validate that the ID token is present on all steps that depend on it, navigating back to
     * the Create Account screen if missing
     */
    if (stepsWhichNeedIdToken.includes(currentStepId) && !idToken) {
      goToSignIn();
      return;
    }

    /*
     * For each step, validate previous steps against their validation schema, navigating back to
     * the first step that failed validation if errors were found
     */
    switch (currentStepId) {
      case Steps.ProviderDetails.Step.id:
        break;
      case Steps.Review.Step.id:
        validateStep(Steps.ProviderDetails.validationSchema, Steps.ProviderDetails.Step.id);
        break;
      case Steps.Done.Step.id:
        break;
      default:
        throw new Error(`Unknown step: ${currentStepId}`);
    }
  }, [
    currentStepId,
    dispatch,
    formValues,
    idToken,
    navigateToStep,
    validateStep,
    state,
    goToSignIn
  ]);
}
